import { useId, useState, type ReactNode } from 'react';
import { useDeactivateUser, useInviteStaff, useReactivateUser, useUsers } from '../../api/users';
import { errorMessage } from '../../api/client';
import { displayName, ROLE_LABELS, type StaffRole, type User } from '../../api/types';
import { can, useMe } from '../../auth/AuthContext';
import {
  Alert,
  Badge,
  Button,
  Modal,
  SelectField,
  TextField,
  formatDate,
  statusLabel,
  statusTone,
  PageHeader,
  Section,
} from '../../components/ui';
import { EmptyState, ErrorState, SkeletonTable } from '../../components/states';
import { DataTable } from '../../components/table/DataTable';

const STAFF_ROLES: StaffRole[] = ['admin', 'product_manager', 'sales_lead', 'support'];

/** Enough to send an invitation to; the API validates properly. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Staff accounts and their roles. Admins invite (a temporary password is emailed
 * password); the role is chosen on the invitation and is not edited from the list.
 */
export function StaffTeam() {
  const me = useMe();
  const canManage = can(me, 'users:roles');
  const canSwitch = can(me, 'users:support');
  const staff = useUsers({ userType: 'staff', limit: 100 });
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: ReactNode } | null>(null);
  const [inviting, setInviting] = useState(false);

  const page = staff.data;

  return (
    <>
      <PageHeader title="Team" />
      {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}

      <Section
        title="Staff accounts"
        meta={page ? `${page.pagination.total} accounts` : undefined}
        actions={
          canManage ? (
            <Button className="btn--small" onClick={() => setInviting(true)}>
              Invite staff member
            </Button>
          ) : undefined
        }
      >
        {staff.isError ? (
          <ErrorState
            error={staff.error}
            onRetry={() => staff.refetch()}
            retrying={staff.isFetching}
          />
        ) : null}
        {staff.isPending ? <SkeletonTable rows={3} cols={6} /> : null}
        {page && page.data.length === 0 ? (
          <EmptyState
            title="No staff accounts yet"
            body="Staff accounts appear here once an admin invites the first one."
            action={
              canManage ? (
                <Button onClick={() => setInviting(true)}>Invite staff member</Button>
              ) : null
            }
          />
        ) : null}
        {page && page.data.length > 0 ? (
          <DataTable
            label="Staff accounts"
            columns={[
              { key: 'name', header: 'Name' },
              { key: 'email', header: 'Email' },
              { key: 'role', header: 'Role' },
              { key: 'status', header: 'Status' },
              { key: 'sign_in', header: 'Sign-in' },
              { key: 'added', header: 'Added' },
              { key: 'actions', header: 'Actions', srOnly: true },
            ]}
          >
            {page.data.map((u) => (
              <StaffRow
                key={u.id}
                user={u}
                switchable={canSwitch && u.id !== me.user.id}
                onChanged={(text, kind = 'success') => setNotice({ kind, text })}
              />
            ))}
          </DataTable>
        ) : null}
      </Section>

      {canManage && inviting ? (
        <InviteStaffModal
          onClose={() => setInviting(false)}
          onDone={(user) => {
            setInviting(false);
            setNotice({
              kind: 'success',
              text: (
                <>
                  Invited <strong>{displayName(user)}</strong> as {ROLE_LABELS[user.role]}. The
                  provider emailed a temporary password to {user.email}.
                </>
              ),
            });
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Email and role are all an invitation needs; the API derives placeholder
 * names from the address. Mounted only while open, so closing it drops what
 * was typed and any error from the last attempt.
 */
function InviteStaffModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (user: User) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('support');
  const formId = useId();
  const invite = useInviteStaff();
  const error = invite.error ? errorMessage(invite.error) : null;
  const valid = EMAIL.test(email.trim());

  const close = () => {
    if (!invite.isPending) onClose();
  };
  const submit = () => {
    if (!valid || invite.isPending) return;
    invite.mutate({ email: email.trim(), role }, { onSuccess: onDone });
  };

  return (
    <Modal
      open
      onClose={close}
      title="Invite staff member"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={close} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={invite.isPending} disabled={!valid}>
            Send invitation
          </Button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form
        id={formId}
        className="form"
        aria-label="Invite a staff member"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        noValidate
      >
        <TextField
          label="Email"
          name="inviteEmail"
          type="email"
          autoComplete="off"
          autoFocus
          required
          value={email}
          hint="They receive a temporary password by email and choose their own at first sign-in."
          onChange={(e) => setEmail(e.target.value)}
        />
        <SelectField
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as StaffRole)}
        >
          {STAFF_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </SelectField>
      </form>
    </Modal>
  );
}

function StaffRow({
  user,
  switchable,
  onChanged,
}: {
  user: User;
  switchable: boolean;
  onChanged: (text: ReactNode, kind?: 'success' | 'error') => void;
}) {
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();

  const toggle = () => {
    const onSuccess = (next: User) =>
      onChanged(
        <>
          <strong>{displayName(next)}</strong>{' '}
          {next.isDeactivated ? 'is deactivated' : 'is active again'}.
        </>,
      );
    if (user.isDeactivated) reactivate.mutate(user.id, { onSuccess });
    else deactivate.mutate(user.id, { onSuccess });
  };
  const toggling = deactivate.isPending || reactivate.isPending;
  const error = deactivate.error ?? reactivate.error;

  return (
    <>
      <tr>
        <td>{displayName(user)}</td>
        <td>{user.email}</td>
        <td>
          <Badge tone="accent">{ROLE_LABELS[user.role]}</Badge>
        </td>
        <td>
          <Badge tone={statusTone(user.status)}>{statusLabel(user.status)}</Badge>
        </td>
        <td>
          {user.isDeactivated ? (
            <Badge tone="bad">Deactivated</Badge>
          ) : (
            <Badge tone={user.isProviderEnabled ? 'ok' : 'bad'}>
              {user.isProviderEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
          )}
        </td>
        <td>{formatDate(user.createdAt)}</td>
        <td>
          {switchable ? (
            <div className="row-actions">
              <Button
                variant={user.isDeactivated ? 'secondary' : 'ghost'}
                className="btn--small"
                loading={toggling}
                onClick={toggle}
                aria-label={`${user.isDeactivated ? 'Reactivate' : 'Deactivate'} ${displayName(user)}`}
              >
                {user.isDeactivated ? 'Reactivate' : 'Deactivate'}
              </Button>
            </div>
          ) : null}
        </td>
      </tr>
      {error ? (
        <tr className="table__detail">
          <td colSpan={7}>
            <Alert kind="error">{errorMessage(error)}</Alert>
          </td>
        </tr>
      ) : null}
    </>
  );
}
